import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TSelector } from "./types";

type Resolvable = string | TSelector;

/**
 * Resolves `label` / `description` fields that may be either a literal string
 * or a Pattern B selector (`($) => $.ns["..."]`). Pass the namespaces the
 * selectors live in — each `useTranslation()` call has its own bound set, so
 * the parent component's bindings do not propagate here.
 */
export function useResolvedLabels<T extends { label?: Resolvable; description?: Resolvable }>(
  items: readonly T[] | undefined,
  namespace?: Parameters<typeof useTranslation>[0],
): (T & { label?: string; description?: string })[] {
  const { t } = useTranslation(namespace);
  return useMemo(
    () =>
      (items ?? []).map((o) => ({
        ...o,
        ...(o.label != null && { label: typeof o.label === "function" ? t(o.label) : o.label }),
        ...(o.description != null && {
          description: typeof o.description === "function" ? t(o.description) : o.description,
        }),
      })),
    [items, t],
  );
}
